<template>
    <JackpotBlock
        v-if="showJackpotBlock"
    />

    <NavbarBlock gtmTypePrefix="SubHeader" />

    <TwoSubBetParlayBanner v-if="isMixParlay && isMpTwoSubBetEnabled" />

    <MaxPayout v-if="isShowMpMaxPayout" />

    <MpRecommendation
        v-if="isMixParlay && isMpCombinationEnabled"
        v-gtm:page="'K_MP'"
    />

    <component
        v-bind:is="oddsDisplayComponent"
        v-if="oddsDisplayComponent"
        v-bind:key="oddsDisplayComponentKey"
    />
</template>

<script lang="ts" setup>
    import { storeToRefs } from 'pinia';
    import { computed, watch } from 'vue';
    import { useRoute } from 'vue-router';
    import TwoSubBetParlayBanner from '@/components/event/TwoSubBetParlayBanner.vue';
    import JackpotBlock from '@/components/jackpot/JackpotBlock.vue';
    import MaxPayout from '@/components/maxPayout/MaxPayout.vue';
    import MpRecommendation from '@/components/mpRecommendation/MpRecommendation.vue';
    import NavbarBlock from '@/components/navbarBlock/NavbarBlock.Asia.vue';
    import OddsDisplayBlock from '@/components/oddsDisplay/OddsDisplayBlock.vue';
    import OutrightOddsDisplayBlock from '@/components/oddsDisplay/outright/OutrightOddsDisplayBlock.vue';
    import { useMyBets } from '@/composable/useMyBets';
    import { usePlatform } from '@/composable/usePlatform';
    import { useTitle } from '@/composable/useTitle';
    import { MarketPage, PageType, PromotionZone, SiteStyle, SportType } from '@/interface/enum';
    import { useAsiaSportMarketInfoStore } from '@/store/asiaSportMarketInfoStore';
    import { useOddsDisplaySettingsStore } from '@/store/oddsDisplaySettingsStore';
    import { useSportLeaguePageStore } from '@/store/sportLeaguePageStore';
    import { useToggleStore } from '@/store/toggleStore';

    const { oddsDisplayMode } = storeToRefs(useOddsDisplaySettingsStore());
    const { promotionZone, sportType, marketPage, eventDate, isMixParlay } = storeToRefs(useAsiaSportMarketInfoStore());
    const oddsDisplayComponent = computed(() => {
        const doubleLineMarketPages = [
            MarketPage.Live,
            MarketPage.Today,
            MarketPage.EarlyMarket,
            MarketPage.MixParlay,
            MarketPage.MyFavorites,
            MarketPage.All,
        ];
        if (doubleLineMarketPages.includes(marketPage.value)) {
            return OddsDisplayBlock;
        }
        if (marketPage.value === MarketPage.Outright) {
            return OutrightOddsDisplayBlock;
        }
        return null;
    });
    const isShowMpMaxPayout = computed(() => marketPage.value === MarketPage.MixParlay);

    useTitle(PageType.Home);

    const { onOpenMyBets, safariBackPageCallBack } = usePlatform();
    safariBackPageCallBack();

    const { openMyBets } = useMyBets();
    onOpenMyBets(openMyBets);

    const route = useRoute();
    const { setSportLeaguePage } = useSportLeaguePageStore();
    const { setPromotionZone } = useAsiaSportMarketInfoStore();

    const param = computed(() => route.params);
    const query = computed(() => route.query);

    watch([param, query], () => {
        if (promotionZone.value === PromotionZone.SportLeague) {
            const sportType = SportType.fromRoute(route.params.sportRouteName?.toString() ?? '');

            const leagueIds = route.query.leagueIds?.toString().split(',').map(Number) ?? [];
            const leagueNames = route.query.leagueNames?.toString().split(',') ?? [];

            setSportLeaguePage(sportType, leagueIds, leagueNames);

            setPromotionZone(PromotionZone.SportLeague);
        }
    });

    const { isMpTwoSubBetEnabled, isMpCombinationEnabled, isJackpotEnabled } = storeToRefs(useToggleStore(SiteStyle.Asia));

    const oddsDisplayComponentKey = computed(() => {
        const baseKey = `${promotionZone.value}_${sportType.value}_${marketPage.value}_${eventDate.value}_${oddsDisplayMode.value}`;

        if (promotionZone.value === PromotionZone.SportLeague) {
            return `${baseKey}_${route.query.leagueIds}_${route.query.leagueNames}`;
        }

        return baseKey;
    });

    const showJackpotBlock = computed(() => isJackpotEnabled.value && (
        promotionZone.value === PromotionZone.EFootball
        || promotionZone.value === PromotionZone.SimulationSports
    ));
</script>
